import React, { useState, useEffect, useRef } from 'react';

import { BiblePassagePicker } from './BiblePassagePicker';
import { BIBLE_BOOKS } from './bibleBooks';
import styles from './BiblePassageInput.module.scss';

export interface BiblePassageInputProps {
	value: string;
	onChange: (value: string) => void;
	onError?: () => void;
	error?: boolean;
	placeholder?: string;
	name?: string;
	id?: string;
}

export const BiblePassageInput: React.FC<BiblePassageInputProps> = (props) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [pickerActiveIndex, setPickerActiveIndex] = useState(0);
	const [bookError, setBookError] = useState(false);
	const [pickerDismissed, setPickerDismissed] = useState(false);
	const [isFocused, setIsFocused] = useState(false);

	const isBookPhase =
		!(/\s\d/.test(props.value)) && !props.value.includes(':');

	// Show full list on focus with no input; filter as user types
	const pickerBooks =
		isBookPhase && !pickerDismissed && isFocused
			? props.value.length === 0
				? BIBLE_BOOKS
				: BIBLE_BOOKS.filter((b) => {
						const lower = b.toLowerCase();
						const q = props.value.toLowerCase();
						if (lower.startsWith(q)) return true;
						const withoutNumber = lower.replace(/^\d+\s+/, '');
						return withoutNumber !== lower && withoutNumber.startsWith(q);
					})
			: [];

	useEffect(() => {
		setPickerActiveIndex(0);
	}, [pickerBooks.length]);

	// Force re-render on resize so the portal anchor rect stays current
	const [, setResizeTick] = useState(0);
	const isPickerOpen = pickerBooks.length > 0;
	useEffect(() => {
		if (!isPickerOpen) return;
		const handleResize = () => setResizeTick((n) => n + 1);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [isPickerOpen]);

	function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
		setBookError(false);
		setPickerDismissed(false);
		props.onChange(e.target.value);
	}

	function handleFocus() {
		setPickerDismissed(false);
		setIsFocused(true);
	}

	function handleBlur() {
		setIsFocused(false);
	}

	function handleBookSelect(book: string) {
		props.onChange(book + ' ');
		setPickerDismissed(true);
		setPickerActiveIndex(0);
		inputRef.current?.focus();
	}

	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key === 'Escape' && pickerBooks.length > 0) {
			e.preventDefault();
			setPickerDismissed(true);
			return;
		}
		if (pickerBooks.length > 0) {
			if (e.key === 'ArrowDown') {
				e.preventDefault();
				setPickerActiveIndex((i) => (i + 1) % pickerBooks.length);
				return;
			}
			if (e.key === 'ArrowUp') {
				e.preventDefault();
				setPickerActiveIndex(
					(i) => (i - 1 + pickerBooks.length) % pickerBooks.length,
				);
				return;
			}
			if (e.key === 'Enter') {
				e.preventDefault();
				handleBookSelect(pickerBooks[pickerActiveIndex]);
				return;
			}
		}
		if (e.key === ' ' && isBookPhase) {
			const typed = props.value.trim();
			const match = BIBLE_BOOKS.find(
				(b) => b.toLowerCase() === typed.toLowerCase(),
			);
			if (match) {
				// Exact match — auto-correct casing and dismiss picker
				e.preventDefault();
				props.onChange(match + ' ');
				setBookError(false);
				setPickerDismissed(true);
			}
			// No exact match — let space through so "1 " keeps showing "1 Samuel" etc.
		}
	}

	const hasError = props.error || bookError;

	// Measure input position fresh each render so portal placement is always current
	const anchorRect =
		pickerBooks.length > 0 && inputRef.current
			? inputRef.current.getBoundingClientRect()
			: null;

	return (
		<div className={styles['wrapper']}>
			{pickerBooks.length > 0 && anchorRect && (
				<BiblePassagePicker
					books={pickerBooks}
					activeIndex={pickerActiveIndex}
					onSelect={handleBookSelect}
					anchorRect={anchorRect}
				/>
			)}
			<div className={[styles['field-container'], hasError ? styles['error'] : ''].filter(Boolean).join(' ')}>
				<input
					ref={inputRef}
					className={styles['input']}
					id={props.id ?? 'bibleReference'}
					name={props.name ?? 'bibleReference'}
					placeholder={props.placeholder ?? 'Genesis 1:1-10'}
					autoComplete="off"
					value={props.value}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					onFocus={handleFocus}
					onBlur={handleBlur}
				/>
			</div>
		</div>
	);
};
